import { NavConfig } from '@pressreader/navigation-types';

import type { NavigateFailedPayload } from '../store';

/**
 * Whether a resolved route is the page already on screen with changed parameters, so the view
 * transition (navigateBegan/navigateDone) is skipped and only the nav config is refreshed.
 *
 * Both the cache key and the view must match. The key alone is not enough: a route that keeps the
 * key of the page it was reached from while resolving to another view is a real navigation, and
 * skipping it would leave the previous view mounted under the new config.
 */
export function shouldSkipNavigation(prevConfig: NavConfig | null, config: NavConfig): boolean {
    if (!prevConfig?.cacheKey) {
        return false;
    }

    return prevConfig.cacheKey === config.cacheKey && prevConfig.viewName === config.viewName;
}

// A view's un-cancelled load saga can finish AFTER a newer navigation replaced it (e.g. the
// article not-available redirect) and echo a `navigateDone`/`navigateFailed` for the superseded
// config; applying it would flip the rendered view back behind the current URL (#174564).
// Staleness is only judged when both ids are present — id-less configs pass through for
// orchestrations that don't mint route ids.
// Saga-side complement: `waitNavigationInProgressFinish` (store/sagas.utils.ts) awaits a navigation's
// terminal action by config-shape predicate; this helper id-checks a single action for supersession.
export function isStaleNavigation(config: NavConfig, currentNavId: string | null): boolean {
    return !!config.id && !!currentNavId && config.id !== currentNavId;
}

export function isNavigateFailedPayload(payload: unknown): payload is NavigateFailedPayload {
    return typeof payload === 'object' && payload !== null && 'config' in payload && (payload as NavigateFailedPayload).navigateFailed === true;
}
